import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { useIsBelow } from "@/hooks/useBreakpoint";
import { useVisualViewportVars } from "@/hooks/useVisualViewportVars";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  onOpenAutoFocus,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
}) {
  const contentRef = React.useRef<HTMLDivElement>(null);

  /**
   * Radix's default is to focus the first focusable descendant on open. When that
   * is a text input — a filter panel's search box, say — iOS opens the on-screen
   * keyboard before the sheet has finished positioning, and the keyboard then
   * covers the sheet it was summoned from.
   *
   * Focus still has to move *into* the dialog or keyboard and screen-reader users
   * lose their place, so this focuses the content container rather than skipping
   * autofocus altogether. A caller that genuinely wants a field focused can pass
   * its own handler and opt out of this.
   */
  const handleOpenAutoFocus = React.useCallback(
    (event: Event) => {
      if (onOpenAutoFocus) {
        onOpenAutoFocus(event);
        return;
      }
      event.preventDefault();
      contentRef.current?.focus();
    },
    [onOpenAutoFocus]
  );

  // Publishes --vv-height / --vv-offset-top for the left/right variants' phone-tier
  // height below. Mounted only while the sheet is open (Radix renders content on
  // demand), so the enable window is already the open window.
  useVisualViewportVars(useIsBelow("md"));

  /**
   * Radix can leave `pointer-events: none` on <body> after a close animation,
   * which makes the whole page unclickable with no visible cause. The upstream
   * issue (radix-ui/primitives#1241) is still open and is reportedly triggered by
   * a close animation — which every sheet has by definition.
   *
   * Cleared on unmount, and only when no dialog is still open: the app stacks
   * modals deliberately in several places, and unlocking the body while an outer
   * one is open would be its own bug.
   */
  React.useEffect(
    () => () => {
      if (typeof document === "undefined") return;
      const stillOpen = document.querySelector('[role="dialog"][data-state="open"]');
      if (!stillOpen && document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    },
    []
  );

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={contentRef}
        data-slot="sheet-content"
        tabIndex={-1}
        onOpenAutoFocus={handleOpenAutoFocus}
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          // Left/right take their phone-tier height from the *visual* viewport.
          //
          // The comment that used to sit here said they needed no treatment, because
          // `inset-y-0` pins them to the viewport's edges and that survives collapsing
          // browser chrome. True, and beside the point: the on-screen keyboard does not
          // shrink the layout viewport at all, and `fixed` is laid out against the
          // layout viewport — so a keyboard raised inside one of these sheets leaves its
          // lower portion behind the keyboard with nothing able to scroll it into view.
          // The left sheet is the app's mobile nav drawer and it contains a patient
          // search field, so this is a real path, not a hypothetical.
          //
          // `bottom-auto` is required alongside: `inset-y-0` sets both edges, and a box
          // with top, bottom AND height over-constrains and ignores the height.
          // Everything is `max-md:`-scoped, so at `md` and up these are byte-identical.
          side === "right" &&
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm max-md:top-visual-viewport max-md:bottom-auto max-md:h-visual-viewport",
          side === "left" &&
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm max-md:top-visual-viewport max-md:bottom-auto max-md:h-visual-viewport",
          side === "top" &&
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto max-h-dvh overflow-y-auto border-b",
          side === "bottom" &&
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto max-h-dvh overflow-y-auto border-t",
          className
        )}
        {...props}
      >
        {children}
        {/* The icon is 16px, so the button was a 16px tap target. Grown to 44px
            below `md` only — this is a raw Radix Close rather than the shared
            Button, so the touch-target work in U15 does not reach it. Desktop
            geometry is deliberately unchanged. */}
        <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none max-md:-m-3 max-md:flex max-md:size-11 max-md:items-center max-md:justify-center">
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
